'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useTranslation } from '@/lib/i18n'
import { streamPortalAsk, useKnowledgeAssistant } from '@/lib/knowledge/portal-api'
import type { AssistantAvailability, KnowledgePortalSurface } from '@/lib/knowledge/portal-types'
import { cn } from '@/lib/utils'
import { AskPanel, type AskSessions } from './ask-panel'
import { useKnowledgeAskStore } from './ask-store'
import { useAnswerSession, type AskStart } from './use-answer-session'

/**
 * The surfaces this person can ask on right now: the employee assistant when it
 * is on, and the manager assistant when it is on AND the manager view is (the
 * server already answers `manager: null` for a person with no reportees).
 */
export function askSurfaces(availability: AssistantAvailability, managerView: boolean): KnowledgePortalSurface[] {
  const surfaces: KnowledgePortalSurface[] = []
  if (availability.employee) surfaces.push('employee')
  if (managerView && availability.manager) surfaces.push('manager')
  return surfaces
}

/** The two conversations, one per surface; they outlive the drawer, so closing it keeps them. */
function useAskSessions(locale: string, onRemaining: (surface: KnowledgePortalSurface, value: number) => void): AskSessions {
  const startEmployee = useCallback(
    ({ question, conversationId, signal, onEvent }: AskStart) =>
      streamPortalAsk({ question, surface: 'employee', locale, conversationId: conversationId ?? undefined }, { signal, onEvent }),
    [locale],
  )
  const startManager = useCallback(
    ({ question, conversationId, signal, onEvent }: AskStart) =>
      streamPortalAsk({ question, surface: 'manager', locale, conversationId: conversationId ?? undefined }, { signal, onEvent }),
    [locale],
  )
  const employee = useAnswerSession(startEmployee, (done) => {
    if (done.remainingToday !== null) onRemaining('employee', done.remainingToday)
  })
  const manager = useAnswerSession(startManager, (done) => {
    if (done.remainingToday !== null) onRemaining('manager', done.remainingToday)
  })
  return { employee, manager }
}

/** The floating widgets' shared signal (components/fab-stack): one open widget hides the others. */
const FAB_OPEN = 'fab-widget:open'
const FAB_CLOSE = 'fab-widget:close'
const FAB_ID = 'knowledge'

/**
 * The portal's policy assistant, opened the way the main application opens its
 * own: the floating orb at the inline-end bottom of every portal page (the
 * main app's floating widget — a white circle in a soft colour glow — with the
 * robot), the "Ask AI" button in each portal header (`KnowledgeAskAiButton`),
 * and any question asked elsewhere on the page (`openAssistant({ question })`).
 * All of them open the same side drawer from the inline end (the left, in
 * Arabic), full screen on a phone. Focus moves into the drawer on open and back
 * to whatever opened it on close; Escape closes it. The orb steps aside while
 * the drawer, or another floating widget, is open.
 *
 * Renders nothing when no assistant is available (both off, the knowledge
 * module off for the company, or the request failed); it tells the header
 * buttons so through the shared store.
 */
export function KnowledgeAskLauncher() {
  const { t, locale } = useTranslation()
  const { availability } = useKnowledgeAssistant(locale)
  const managerView = useKnowledgeAskStore((s) => s.managerView)
  const open = useKnowledgeAskStore((s) => s.open)
  const pending = useKnowledgeAskStore((s) => s.pending)
  const openAssistant = useKnowledgeAskStore((s) => s.openAssistant)
  const closeAssistant = useKnowledgeAskStore((s) => s.closeAssistant)
  const clearPending = useKnowledgeAskStore((s) => s.clearPending)
  const setAvailable = useKnowledgeAskStore((s) => s.setAvailable)
  const [remaining, setRemaining] = useState<Partial<Record<KnowledgePortalSurface, number>>>({})
  const [chosen, setChosen] = useState<KnowledgePortalSurface | null>(null)
  const [otherWidgetOpen, setOtherWidgetOpen] = useState(false)
  const fabRef = useRef<HTMLButtonElement>(null)
  const interactedOutside = useRef(false)
  const sessions = useAskSessions(locale, (surface, value) => setRemaining((prev) => ({ ...prev, [surface]: value })))

  const surfaces = askSurfaces(availability, managerView)
  const available = surfaces.length > 0
  const defaultSurface: KnowledgePortalSurface = surfaces.includes('manager') ? 'manager' : (surfaces[0] ?? 'employee')
  const surface = chosen && surfaces.includes(chosen) ? chosen : defaultSurface
  const shown = open && available

  useEffect(() => {
    setAvailable(available)
  }, [available, setAvailable])
  // Leaving the portal (the layout goes) closes the drawer and drops a question nobody sent.
  useEffect(() => () => useKnowledgeAskStore.setState({ available: false, open: false, pending: null, opener: null }), [])

  // A question asked outside the drawer: a new conversation whose first message it is.
  const latest = useRef({ sessions, availability, remaining, surfaces, defaultSurface })
  useEffect(() => {
    latest.current = { sessions, availability, remaining, surfaces, defaultSurface }
  })
  const handled = useRef<number | null>(null)
  useEffect(() => {
    if (!pending || !available || handled.current === pending.id) return
    handled.current = pending.id
    clearPending(pending.id)
    const now = latest.current
    const target = pending.surface && now.surfaces.includes(pending.surface) ? pending.surface : now.defaultSurface
    setChosen(target)
    const assistant = now.availability[target]
    // None left today: the drawer opens on the hello, which says so.
    if (!assistant || (now.remaining[target] ?? assistant.remainingToday) <= 0) return
    const session = now.sessions[target]
    session.reset()
    void session.ask(pending.question)
  }, [pending, available, clearPending])

  // Tell the other floating widgets, and listen to theirs.
  const announced = useRef(false)
  useEffect(() => {
    if (shown === announced.current) return
    announced.current = shown
    window.dispatchEvent(new CustomEvent(shown ? FAB_OPEN : FAB_CLOSE, { detail: FAB_ID }))
  }, [shown])
  useEffect(() => {
    const onOpen = (event: Event) => {
      if ((event as CustomEvent).detail !== FAB_ID) setOtherWidgetOpen(true)
    }
    const onClose = (event: Event) => {
      if ((event as CustomEvent).detail !== FAB_ID) setOtherWidgetOpen(false)
    }
    window.addEventListener(FAB_OPEN, onOpen)
    window.addEventListener(FAB_CLOSE, onClose)
    return () => {
      window.removeEventListener(FAB_OPEN, onOpen)
      window.removeEventListener(FAB_CLOSE, onClose)
    }
  }, [])

  if (!available) return null
  const hidden = shown || otherWidgetOpen
  const label = t('knowledgePortal.launcher.open')

  return (
    <>
      <div
        data-slot="ask-fab"
        data-state={hidden ? 'hidden' : 'shown'}
        className={cn(
          'group/ask-fab fixed bottom-6 end-6 z-40 size-12 transition-opacity duration-300 motion-reduce:transition-none',
          hidden ? 'pointer-events-none opacity-0' : 'opacity-100',
        )}
      >
        {/* The main app's floating widget: a soft, slowly turning colour glow behind a white circle. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-3 opacity-80 transition-opacity duration-[380ms] ease-out group-hover/ask-fab:opacity-100 motion-reduce:transition-none"
        >
          <span className="pip-aura-active block h-full w-full rounded-[32px] motion-reduce:animate-none!" />
        </span>
        <Button
          ref={fabRef}
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
          aria-expanded={shown}
          tabIndex={hidden ? -1 : undefined}
          onClick={(event) => openAssistant({ opener: event.currentTarget })}
          className="relative z-10 size-12 rounded-full border-border bg-background text-foreground shadow-sm hover:bg-background dark:bg-background dark:hover:bg-accent"
        >
          <Bot className="size-5" aria-hidden="true" />
        </Button>
      </div>
      <Sheet open={shown} onOpenChange={(next) => (next ? openAssistant() : closeAssistant())}>
        <SheetContent
          side="end"
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-full max-w-none gap-0 p-0 sm:w-[max(30rem,42vw)] sm:max-w-[calc(100vw-2rem)]"
          onOpenAutoFocus={(event) => {
            // Into the question box when it can take a question, else the first control.
            event.preventDefault()
            interactedOutside.current = false
            const panel = event.target instanceof HTMLElement ? event.target : null
            const target =
              panel?.querySelector<HTMLElement>('textarea:not([disabled])') ?? panel?.querySelector<HTMLElement>('button:not([disabled])')
            target?.focus()
          }}
          onInteractOutside={(event) => {
            // Closed by a click elsewhere on the page: focus stays where that click put it. (Focus
            // moving out does not close the drawer; the sheet has already prevented that one.)
            if (!event.defaultPrevented) interactedOutside.current = true
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (interactedOutside.current) {
              interactedOutside.current = false
              return
            }
            const opener = useKnowledgeAskStore.getState().opener
            ;(opener?.isConnected ? opener : fabRef.current)?.focus()
          }}
        >
          <AskPanel
            availability={availability}
            surfaces={surfaces}
            surface={surface}
            onSurfaceChange={setChosen}
            sessions={sessions}
            remaining={remaining}
            onClose={closeAssistant}
          />
        </SheetContent>
      </Sheet>
    </>
  )
}
